import React, { useEffect, useState } from "react";
import {
  Calendar,
  ArrowUpRight,
  CircleIcon,
  CalendarCheck,
  StopCircle,
  PlayCircle,
  RefreshCw,
  X,
  Save,
} from "lucide-react";
import {
  Box,
  ThemeProvider,
  createTheme,
  Paper,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import ScheduleWizard from "./EditScheduleDialog";
import Activity from "./Activity";
import { jwtDecode } from "jwt-decode";
import UnauthorizedPage from "../UnauthorizedPage";
const theme = createTheme({
  palette: {
    primary: {
      main: "#003366",
    },
  },
});

const apiUrl = process.env.REACT_APP_API_URL;

// Animated counter component that smoothly counts up to the target value
const AnimatedCounter = ({ value, duration = 2000, isPercentage = false }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (value === undefined || value === null) return;

    let startTime;
    const startValue = 0;
    const endValue = parseFloat(value);

    const animation = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // Easing function for smoother animation
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentCount = startValue + (endValue - startValue) * easedProgress;

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animation);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animation);
  }, [value, duration]);

  return (
    <span>{isPercentage ? `${count.toFixed(1)}%` : Math.round(count)}</span>
  );
};

// Enhanced loading skeleton with professional animation effects
const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
    {[1, 2, 3].map((index) => (
      <div
        key={index}
        className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-6 overflow-hidden"
      >
        {/* Shimmering gradient effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="w-full h-full transform -translate-x-full"
            style={{
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%)",
              animation: "shine 2s infinite linear",
            }}
          />
        </div>

        <div className="relative space-y-6">
          {/* Icon placeholder with subtle pulse */}
          <div className="flex justify-center">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse" />
          </div>

          {/* Title placeholder */}
          <div className="space-y-2">
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md mx-auto w-3/4" />
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md mx-auto w-2/3 opacity-70" />
          </div>

          {/* Value placeholder */}
          <div className="flex justify-center items-center mt-4">
            <div className="h-10 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default function ResourceScheduler() {
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshingOne, setIsRefreshingOne] = useState({});
  const [refresh, setRefresh] = useState(false);
  let accessToken = useSelector((state) => state.accessToken.value);
  const [editSchedule, setEditSchedule] = useState(false);
  const [editScheduleAccount, setEditScheduleAccount] = useState();
  const [viewActivity, setViewActivity] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [error, setError] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [message, setMessage] = useState("");
  const [task, setTask] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const filteredData = data?.filter((row) =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: data?.length,
    scheduled: data?.filter((acc) => acc.scheduledescription != "Disabled")
      .length,
    disabled: data?.filter((acc) => acc.scheduledescription == "Disabled")
      .length,
  };
  let idToken = useSelector((state) => state.idToken.value);
  const decoded = jwtDecode(idToken);

  const [userName, setUserName] = useState(
    `${decoded.given_name} ${decoded.family_name}`
  );

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const getData = async () => {
    setIsLoading(true);
    const query = `
  query MyQuery {
  getAccounts {
    AccountName
    AccountNo
    id
    region
    lastUpdated
    portalUrl
    scheduledescription
    startecscronexpression
    startrdscronexpression
    status
    stopcronexpression
    timezone
    startTime
    stopTime
    days
    stateStop
    stateStart
    days
    disableReason
    rollbackDate
  }
}
`;
    await axios
      .post(
        apiUrl,
        {
          query: query,
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        ) {
          setAuthError("Unauthorized");
        }
        if (response.data.data && response.data.data.getAccounts)
          setData(response.data.data.getAccounts);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, [refresh]);

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    await getData();
    setIsRefreshing(false);
  };

  const selectStyles = {
    minWidth: 120,
    m: 1,
  };
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(data.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev?.filter((rowId) => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const getDataOne = async (id) => {
    const query = `
  mutation MyMutation($id:String) {
  updateAccountStatus(id: $id) {
    AccountName
    AccountNo
    id
    lastUpdated
    portalUrl
    status
    region
  }
}
`;
    const variables = {
      id: id,
    };
    await axios
      .post(
        apiUrl,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        ) {
          setAuthError("Unauthorized");
        }
        if (response.data.data && response.data.data.updateAccountStatus) {
          setData((prevDetails) =>
            prevDetails.map((item) =>
              item.id === id
                ? { ...item, ...response.data.data.updateAccountStatus }
                : item
            )
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleRefreshOne = async (id) => {
    setIsRefreshingOne((prev) => ({ ...prev, [id]: true }));
    await getDataOne(id);
    setIsRefreshingOne((prev) => ({ ...prev, [id]: false }));
  };

  const getAccountDetails = () => {
    const accountDetails = data
      ?.filter((acc) => selectedRows.includes(acc.id))
      .map((acc) => ({
        AccountName: acc.AccountName,
        AccountNo: acc.AccountNo,
        region: acc.region,
      }));
    return accountDetails;
  };

  const getAccountNames = () => {
    const accountDetails = data
      ?.filter((acc) => selectedRows.includes(acc.id))
      .map((acc) => acc.AccountName)
      .toString();
    return accountDetails;
  };

  const startSchedule = async () => {
    setIsSubmitting(true);
    const data = getAccountDetails();
    const query = `
  mutation MyMutation($accountnames:String $accountNos:String $region:String $username:String) {
  startAutomation(accountnames: $accountnames, accountNos: $accountNos, region:$region , username: $username)
}
  `;
    for (const item of data) {
      const variables = {
        accountnames: item.AccountName,
        accountNos: item.AccountNo,
        username: userName,
        region: item.region,
      };
      await axios
        .post(
          apiUrl,
          {
            query: query,
            variables: variables,
          },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          if (
            response.data.errors &&
            response.data.errors[0].errorType == "Unauthorized"
          ) {
            setOpenSnack(true);
            setMessage(
              "Start/Stop resource access is not granted to your account."
            );
            setError(true);
          }
          if (response.data.data && response.data.data.startAutomation) {
            setOpenSnack(true);
            setMessage(response.data.data.startAutomation);
            setError(false);
          }
        })
        .catch((error) => {
          setOpenSnack(true);
          setMessage(error);
          setError(true);
        });
    }
    setIsSubmitting(false);
  };
  const stopSchedule = async () => {
    setIsSubmitting(true);
    const data = getAccountDetails();
    const query = `
    mutation MyMutation($accountnames:String $accountNos:String $region:String $username:String) {
    stopAutomation(accountnames: $accountnames, accountNos: $accountNos, region:$region username: $username)
  }
    `;
    for (const item of data) {
      const variables = {
        accountnames: item.AccountName,
        accountNos: item.AccountNo,
        username: userName,
        region: item.region,
      };
      await axios
        .post(
          apiUrl,
          {
            query: query,
            variables: variables,
          },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          console.log(response)
          if (
            response.data.errors &&
            response.data.errors[0].errorType == "Unauthorized"
          ) {
            setOpenSnack(true);
            setMessage(
              "Start/Stop resource access is not granted to your account."
            );
            setError(true);
          }
          if (response.data.data && response.data.data.stopAutomation) {
            setOpenSnack(true);
            setMessage(response.data.data.stopAutomation);
            setError(false);
          }
        })
        .catch((error) => {
          setOpenSnack(true);
          setMessage(error);
          setError(true);
        });
    }
    setIsSubmitting(false);
  };

  const handleConfirm = () => {
    if (task === "start") {
      startSchedule();
    } else {
      stopSchedule();
    }
    setConfirmDialog(false);
  };

  if (authError == "Unauthorized") {
    return <UnauthorizedPage />;
  } else
    return (
      <ThemeProvider theme={theme}>
        <div className="p-8">
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 2, bgcolor: "#fff", mb: 4 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
              }}
            >
              <button
                onClick={() => setViewActivity(true)}
                className="flex items-center gap-2 text-[#003366] hover:text-[#004488]"
              >
                <Activity size={20} />
                <span>Activity Logs</span>
                <ArrowUpRight size={16} />
              </button>
            </Box>
          </Paper>

          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 transition-all duration-300 ">
                <div className="text-[#003366]">
                  <div className="flex items-center justify-center mb-4">
                    <CircleIcon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2">
                    Total AWS Accounts
                  </h3>
                  <p className="text-center text-sm text-gray-600 mb-4">
                    All registered accounts
                  </p>
                  <div className="text-4xl font-bold text-center text-[#003366]">
                    <AnimatedCounter value={stats.total} isPercentage={false} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 transition-all duration-300 ">
                <div className="text-[#003366]">
                  <div className="flex items-center justify-center mb-4">
                    <CalendarCheck className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2">
                    Scheduled Resources
                  </h3>
                  <p className="text-center text-sm text-gray-600 mb-4">
                    Currently Scheduled resources
                  </p>
                  <div className="text-4xl font-bold text-center text-green-600">
                    <AnimatedCounter
                      value={stats.scheduled}
                      isPercentage={false}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 transition-all duration-300 ">
                <div className="text-[#003366]">
                  <div className="flex items-center justify-center mb-4">
                    <StopCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2">
                    Schedule Disabled
                  </h3>
                  <p className="text-center text-sm text-gray-600 mb-4">
                    Disabled Schedules resources
                  </p>
                  <div className="text-4xl font-bold text-center text-red-600">
                    <AnimatedCounter
                      value={stats.disabled}
                      isPercentage={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg border border-gray-200">
            <div className="p-6">
              <div className="flex justify-between items-center gap-3 mb-4">
                <div className="flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search accounts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366] focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex justify-end gap-3 mb-4">
                    <button
                      onClick={() => {
                        setTask("start");
                        setConfirmDialog(true);
                      }}
                      disabled={selectedRows.length === 0}
                      className={`
                  flex items-center gap-2 px-4 py-2 rounded 
                  ${
                    selectedRows.length === 0
                      ? "bg-green-200 text-green-500 cursor-not-allowed"
                      : "bg-[#4BB543] text-white hover:bg-[#45a33e]"
                  }
                `}
                    >
                      <PlayCircle className="w-4 h-4" />
                      Start
                    </button>

                    <button
                      onClick={() => {
                        setTask("stop");
                        setConfirmDialog(true);
                      }}
                      disabled={selectedRows.length === 0}
                      className={`
                  flex items-center gap-2 px-4 py-2 rounded 
                  ${
                    selectedRows.length === 0
                      ? "bg-red-200 text-red-500 cursor-not-allowed"
                      : "bg-[#C62E2E] text-white hover:bg-[#b22929]"
                  }
                `}
                    >
                      <StopCircle className="w-4 h-4" />
                      Stop
                    </button>

                    <button
                      onClick={handleRefreshAll}
                      className="
                  flex items-center gap-2 px-4 py-2 
                  bg-[#003366] text-white rounded 
                  hover:bg-[#004488] 
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                    >
                      {isRefreshing ? (
                        <CircularProgress
                          size={16}
                          color="inherit"
                          className="mr-2"
                        />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {isRefreshing ? "Refreshing..." : "Refresh All"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#003366]/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        <Checkbox
                          checked={
                            selectedRows.length === data?.length &&
                            data?.length > 0
                          }
                          indeterminate={
                            selectedRows.length > 0 &&
                            selectedRows.length < data?.length
                          }
                          onChange={handleSelectAll}
                          sx={{
                            color: "#003366",
                            "&.Mui-checked": {
                              color: "#003366",
                            },
                          }}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Account Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Account Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Region
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Last Refreshed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Portal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Schedule
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData?.map((row) => (
                      <tr key={row.id} className="hover:bg-[#003366]/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Checkbox
                            checked={selectedRows.includes(row.id)}
                            onChange={() => handleSelectRow(row.id)}
                            sx={{
                              color: "#003366",
                              "&.Mui-checked": {
                                color: "#003366",
                              },
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[#003366] font-medium">
                          {row.AccountName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {row.AccountNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {row.region}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              row.status === "Running"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {row.lastUpdated}
                          <button onClick={() => handleRefreshOne(row.id)}>
                            {isRefreshingOne[row.id] ? (
                              <CircularProgress
                                size={12}
                                color="inherit"
                                className="mr-2 ml-3"
                              />
                            ) : (
                              <RefreshCw className="w-3 h-3 mr-2 ml-3" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a
                            href={row.portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#003366] hover:text-[#004488]"
                          >
                            Open
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditScheduleAccount(row);
                                setEditSchedule(true);
                              }}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                            >
                              <Calendar size={16} className="text-gray-400" />
                            </button>
                            {row.scheduledescription === "Disabled" ? (
                              <Tooltip
                                title={
                                  <div>
                                    <div>
                                      <strong>Schedule Resumes:</strong>{" "}
                                      {row.rollbackDate
                                        ? row.rollbackDate
                                        : "Unknown"}
                                    </div>
                                    <div>
                                      <strong>Reason:</strong>{" "}
                                      {row.disableReason
                                        ? row.disableReason
                                        : "Not specified"}
                                    </div>
                                  </div>
                                }
                                arrow
                                placement="top"
                              >
                                <span>
                                  {row.scheduledescription}
                                </span>
                              </Tooltip>
                            ) : (
                              <span>{row.scheduledescription}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data?.length === 0 && !isLoading && (
                  <div className="text-center py-12">
                    <div className="text-[#003366]">
                      <svg
                        className="w-12 h-12 mx-auto mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                      <p className="text-lg font-semibold">No Data Available</p>
                      <p className="mt-2">
                        Click the Add New button to add your first item
                      </p>
                    </div>
                  </div>
                )}
                {isLoading && (
                  <div className="flex  justify-center items-center">
                    <div className="w-12 h-12 m-10 border-4 border-blue-200 border-t-[#003366] rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Dialog
          open={confirmDialog}
          onClose={() => setConfirmDialog(false)}
          PaperProps={{
            style: {
              borderRadius: "8px",
              padding: "8px",
              maxWidth: "450px",
            },
          }}
        >
          <DialogTitle
            style={{
              padding: "16px 24px",
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#003366",
            }}
          >
            {task === "start" ? "Confirm Start" : "Confirm Stop"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              style={{
                color: "#4a4a4a",
                fontSize: "1rem",
                marginBottom: "8px",
              }}
            >
              {task === "start"
                ? `Starting up resources in ${getAccountNames()} will take about 20 minutes. Proceed?`
                : `Are you sure you want to stop resources in ${getAccountNames()}?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
            <button
              onClick={() => setConfirmDialog(false)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                backgroundColor: "#FFD700",
                color: "#003366",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: 500,
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#FFE44D")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#FFD700")
              }
            >
              <X size={16} />
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                backgroundColor: "#003366",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: 500,
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#004488")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#003366")
              }
              autoFocus
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save size={16} />
              )}
              Confirm
            </button>
          </DialogActions>
        </Dialog>

        <ScheduleWizard
          open={editSchedule}
          onClose={() => setEditSchedule(false)}
          accountData={editScheduleAccount}
          clearAccountData={() => {
            setEditScheduleAccount();
          }}
          refresh={refresh}
          setRefresh={setRefresh}
        />
        <Activity open={viewActivity} onClose={() => setViewActivity(false)} />
        <Snackbar
          sx={{ fontFamily: "Poppins" }}
          open={openSnack}
          autoHideDuration={4000}
          onClose={handleCloseSnack}
        >
          <Alert
            onClose={handleCloseSnack}
            severity={!error ? "success" : "error"}
          >
            {message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
}
